import {
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { useContext, useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useDeviceSize } from '../../hooks/useDeviceSize'

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import SearchInput from '../shared/SearchInput'
import { Button } from '../ui/button'

import { DataTablePagination } from './DataTablePagination' // This component is now responsive

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { AuthContext } from '@/contexts/auth-context'
import PlanUpgradeDialog from '../ui/plan-upgrade-dialog'
import logger from '../../utils/logger'

function DataTable({ columns, data, totalEntries, isFetching = false, clickHandler = () => {} }) {
  const { auth } = useContext(AuthContext)
  const plan = auth?.user?.subscription?.planTier || 'basic'
  const navigate = useNavigate()
  const isLargeDevice = useDeviceSize()
  const [showDeviceWarning, setShowDeviceWarning] = useState(false)
  const [showPlanUpgrade, setShowPlanUpgrade] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()

  const sort = searchParams.get('sort')

  const [sorting, setSorting] = useState(
    sort ? [{ desc: sort.startsWith('-'), id: sort.replace('-', '') }] : []
  )
  const [columnVisibility, setColumnVisibility] = useState({})
  const [rowSelection, setRowSelection] = useState({})

  // Convert API page (1-based) to React Table page (0-based)
  const apiPage = Number(searchParams.get('page')) || 1
  const pageSize = Number(searchParams.get('pageSize')) || 10

  const pagination = {
    pageIndex: apiPage - 1, // Convert 1-based to 0-based
    pageSize: pageSize,
  }

  const totalPageCount = Math.ceil(totalEntries / pagination.pageSize)
  logger.log('total page count =========> ', totalPageCount)

  useEffect(() => {
    if (sorting.length === 0) return

    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev)
      newParams.set('sort', (sorting[0].desc ? '-' : '') + sorting[0].id)
      return newParams
    })
  }, [sorting, setSearchParams])

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    state: {
      sorting,
      columnVisibility,
      rowSelection,
      pagination,
    },
    pageCount: totalPageCount,
    onPaginationChange: (updater) => {
      setRowSelection({})
      if (typeof updater !== 'function') return

      const newPagination = updater(pagination)
      const newApiPage = newPagination.pageIndex + 1 // Convert 0-based to 1-based

      setSearchParams((searchParams) => {
        searchParams.set('page', newApiPage.toString())
        searchParams.set('pageSize', newPagination.pageSize.toString())
        return searchParams
      })
    },
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    manualPagination: true,
  })

  const handleAddNewForm = () => {
    // Check if user is on Basic plan
    if (plan?.toLowerCase() === 'basic') {
      setShowPlanUpgrade(true)
      return
    }

    // Check device size for non-Basic users
    if (!isLargeDevice) {
      setShowDeviceWarning(true)
      return
    }

    navigate('/business/create-form')
  }

  return (
    <div className="space-y-4 p-4 sm:p-6 lg:p-8">
      {/* Top section: Search input and Add New Form button */}
      <div className="mt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex flex-1 items-center gap-4 w-full sm:w-auto">
          <SearchInput placeholder="Search..." className="w-full sm:w-72" debounce />
        </div>
        <div className="flex gap-4 mt-4 sm:mt-0 w-full justify-end sm:w-auto">
          <Button
            onClick={handleAddNewForm}
            className="bg-heading-primary w-full sm:w-auto hover:text-theme-color-800 hover:bg-white hover:border hover:border-theme-color-700 cursor-pointer"
          >
            Add New Form
          </Button>
        </div>
      </div>

      {/* Main data table */}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  className={isFetching ? 'opacity-60' : 'cursor-pointer'}
                  onClick={() => clickHandler(row.original._id)}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No results.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      {/* Pagination component */}
      <DataTablePagination table={table} />

      {/* Add Device Warning Dialog */}
      <Dialog open={showDeviceWarning} onOpenChange={setShowDeviceWarning}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Desktop Required</DialogTitle>
            <DialogDescription>
              Form creation is only available on desktop or larger screen devices. Please switch to
              a desktop device for the best experience with our form editor.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="primary" onClick={() => setShowDeviceWarning(false)}>
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Plan Upgrade Dialog */}
      <PlanUpgradeDialog
        showPlanUpgrade={showPlanUpgrade}
        setShowPlanUpgrade={setShowPlanUpgrade}
        name="Form creation"
        desc="Create unlimited custom forms with advanced features"
      />
    </div>
  )
}

export default DataTable
